import * as React from 'react';
import { Calendar } from './calendar';
import { addDays, subDays, startOfMonth, endOfMonth, format } from 'date-fns';

// DateRange type
interface DateRange {
  from: Date | undefined;
  to?: Date | undefined;
}

// Preset options with logic
const presetOptions: { label: string; getRange: () => DateRange }[] = [
  {
    label: 'Today',
    getRange: () => {
      const d = new Date();
      return { from: d, to: d };
    },
  },
  {
    label: 'Yesterday',
    getRange: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return { from: d, to: d };
    },
  },
  {
    label: 'This week (Sun - Today)',
    getRange: () => {
      const d = new Date();
      const from = new Date(d);
      from.setDate(d.getDate() - d.getDay());
      return { from, to: d };
    },
  },
  {
    label: 'Last week (Sun - Sat)',
    getRange: () => {
      const d = new Date();
      const to = new Date(d);
      to.setDate(d.getDate() - d.getDay() - 1);
      const from = new Date(to);
      from.setDate(to.getDate() - 6);
      return { from, to };
    },
  },
  {
    label: 'Last 7 days',
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - 6);
      return { from, to };
    },
  },
  {
    label: 'Last 28 days',
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - 27);
      return { from, to };
    },
  },
  {
    label: 'Last 30 days',
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - 29);
      return { from, to };
    },
  },
  {
    label: 'Last 90 days',
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - 89);
      return { from, to };
    },
  },
  {
    label: 'Last 12 months',
    getRange: () => {
      const to = new Date();
      const from = new Date();
      from.setFullYear(to.getFullYear() - 1);
      from.setDate(from.getDate() + 1);
      return { from, to };
    },
  },
];

function formatDate(date?: Date) {
  return date ? format(date, 'MMM dd, yyyy') : '--';
}

function getPresetLabel(range: DateRange): string {
  for (const preset of presetOptions) {
    const presetRange = preset.getRange();
    if (
      range.from && range.to &&
      presetRange.from && presetRange.to &&
      range.from.toDateString() === presetRange.from.toDateString() &&
      range.to.toDateString() === presetRange.to.toDateString()
    ) {
      return preset.label;
    }
  }
  return 'Custom';
}

interface DateRangeSelectorProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  onApply?: () => void;
  onCancel?: () => void;
}

export function DateRangeSelector({ value, onChange, onApply, onCancel }: DateRangeSelectorProps) {
  const currentLabel = getPresetLabel(value);
  return (
    <div className="flex flex-col md:flex-row w-full min-w-0">
      {/* Presets sidebar */}
      <div className="min-w-0 md:min-w-[200px] border-b md:border-b-0 md:border-r border-gray-200 p-2">
        <ul className="list-none p-0 m-0">
          <li
            className={`mb-2 text-sm p-2 rounded-md ${
              currentLabel === 'Custom' 
                ? 'font-bold text-orange-500 bg-orange-50' 
                : 'font-medium'
            }`}
          >
            Custom
          </li>
          {presetOptions.map((preset) => (
            <li key={preset.label} className="mb-2">
              <button
                className={`w-full text-xs text-left p-2 rounded-md border-none cursor-pointer ${
                  currentLabel === preset.label 
                    ? 'bg-orange-50 text-orange-500 font-bold' 
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => onChange(preset.getRange())}
              >
                {preset.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      {/* Calendar section */}
      <div className="flex-1 p-4 flex flex-col">
        {/* Start/End date pickers */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1">
            <div className="text-xs text-gray-500 mb-1">Start Date</div>
            <div className="border border-gray-200 rounded-md p-3 bg-gray-50 text-xs font-medium">
              {formatDate(value.from)}
            </div>
          </div>
          <div className="flex-1">
            <div className="text-xs text-gray-500 mb-1">End Date</div>
            <div className="border border-gray-200 rounded-md p-3 bg-gray-50 text-xs font-medium">
              {formatDate(value.to)}
            </div>
          </div>
        </div>
        {/* Calendar */}
        <div className="flex-1">
          <Calendar
            mode="range"
            selected={value}
            onSelect={onChange}
            numberOfMonths={1}
            className="p-3 border text-xs rounded-lg bg-white"
          />
        </div>
        {/* Apply/Cancel buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            className="px-5 text-xs py-2 border-none bg-none text-orange-500 font-medium rounded-md cursor-pointer hover:bg-orange-50"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-5 text-xs py-2 border-none bg-black text-white font-medium rounded-md cursor-pointer hover:bg-orange-600 disabled:opacity-50"
            onClick={onApply}
            disabled={!value.from || !value.to}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
} 